import {
  Navigate,
  Outlet,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectors } from 'darwin_core';

import { PROPERTY_PATH, homePath } from './paths.js';
import { findAccountZone } from './zoneValidation.js';
import LoadingScreen from '../components/LoadingScreen.jsx';

// Layout route for /zone/:homeId/:prtId/:endpointId and its nested action screens
// (status / schedule / boost). It validates the URL against the seeded homes ONCE
// and hands the validated { homeId, zoneId } down through outlet context, so the
// zone screen and every action screen share one ownership check and none of them
// re-parses params or re-coerces the endpoint id.
//
// It must not redirect before the store is seeded. A deep link or a hard refresh
// on a legitimately-owned zone arrives before the REST hello lands; bouncing to
// the Property screen in that window would break bookmarks and refresh. So while
// `hadFirstHello` is false it holds a loading placeholder, and only once the
// account is seeded — and the zone is still not found — does it redirect.
export default function ZoneRoute() {
  const { homeId, prtId, endpointId } = useParams();
  const homes = useSelector((state) => state.homeList.homes);
  const hadFirstHello = useSelector((state) => state.homeList.hadFirstHello);
  const cloudSupported = useSelector((state) =>
    selectors.isCloudSupported(state, homeId),
  );

  if (!hadFirstHello) {
    return <LoadingScreen />;
  }

  const zone = findAccountZone(homes, homeId, prtId, endpointId);
  if (!zone) {
    return <Navigate to={PROPERTY_PATH} replace />;
  }

  // No cloud licence: the app cannot open a zone on such a home at all (its
  // Property screen is replaced by the upgrade notice), so neither can the web —
  // a deep link lands on that notice instead of a dial that could not send.
  if (!cloudSupported) {
    return <Navigate to={homePath(homeId)} replace />;
  }

  // Pass the zone's OWN tuple (typed endpoint), not the string params.
  return <Outlet context={{ homeId, zoneId: zone.zoneId }} />;
}

// Typed accessor for the validated zone context, used by the zone screen and its
// action screens instead of reading useOutletContext() directly.
export function useZoneRouteContext() {
  return useOutletContext();
}
